import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Clock,
  MapPin,
  Play,
  Square,
  Coffee,
  CoffeeIcon,
  AlertCircle,
  CheckCircle,
  X,
  RotateCcw,
} from "lucide-react";
import apiService from "../services/api";

const AttendanceWidget = () => {
  const { t, i18n } = useTranslation();
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [dailySummary, setDailySummary] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [breakReason, setBreakReason] = useState("");
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [showCheckOutConfirm, setShowCheckOutConfirm] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationRetries, setLocationRetries] = useState(0);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Get location with retry logic
  const getLocationWithRetry = (retryCount = 0) => {
    if (!navigator.geolocation) {
      setLocationError(t("attendance.location.notSupported"));
      setLocationLoading(false);
      return;
    }

    setLocationLoading(true);

    // First attempt: high accuracy (longer timeout)
    const timeout = retryCount === 0 ? 20000 : 15000;
    const enableHighAccuracy = retryCount === 0;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        setLocationError(null);
        setLocationLoading(false);
        setLocationRetries(0);
      },
      (error) => {
        if (retryCount < 2) {
          // Retry with lower accuracy
          setLocationRetries(retryCount + 1);
          setTimeout(() => {
            getLocationWithRetry(retryCount + 1);
          }, 1000);
        } else {
          // Final attempt failed
          let errorMessage = t("attendance.location.errors.unknown");

          if (error.code === error.PERMISSION_DENIED) {
            errorMessage = t("attendance.location.errors.permissionDenied");
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            errorMessage = t("attendance.location.errors.positionUnavailable");
          } else if (error.code === error.TIMEOUT) {
            errorMessage = t("attendance.location.errors.timeout");
          }

          setLocationError(errorMessage);
          setLocationLoading(false);
          setLocationRetries(0);
        }
      },
      {
        enableHighAccuracy: enableHighAccuracy,
        timeout: timeout,
        maximumAge: retryCount === 0 ? 0 : 30000, // Allow cached position on retry
      }
    );
  };

  // Get location on component mount
  useEffect(() => {
    getLocationWithRetry();
  }, [t]);

  // Fetch attendance data
  useEffect(() => {
    fetchAttendanceData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchAttendanceData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAttendanceData = async () => {
    try {
      const [statusResponse, summaryResponse] = await Promise.all([
        apiService.getAttendanceStatus(),
        apiService.getDailySummary(),
      ]);

      setAttendanceStatus(statusResponse);
      setDailySummary(summaryResponse);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!location) {
      alert(
        "Location is required for check-in. Please enable location services."
      );
      return;
    }

    setActionLoading(true);
    try {
      await apiService.checkIn({
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        deviceInfo: navigator.userAgent,
      });

      await fetchAttendanceData();
      alert("Successfully checked in!");
    } catch (error) {
      alert(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!location) {
      alert(
        "Location is required for check-out. Please enable location services."
      );
      return;
    }

    setShowCheckOutConfirm(true);
  };

  const confirmCheckOut = async () => {
    setShowCheckOutConfirm(false);

    setActionLoading(true);
    try {
      const response = await apiService.checkOut({
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        deviceInfo: navigator.userAgent,
      });

      await fetchAttendanceData();
      alert(
        `Successfully checked out! Total hours: ${response.summary.totalHours}, Pay: $${response.summary.grossPay}`
      );
    } catch (error) {
      alert(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartBreak = async () => {
    setActionLoading(true);
    try {
      await apiService.startBreak(breakReason);
      await fetchAttendanceData();
      setShowBreakModal(false);
      setBreakReason("");
      alert("Break started!");
    } catch (error) {
      alert(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEndBreak = async () => {
    setActionLoading(true);
    try {
      await apiService.endBreak();
      await fetchAttendanceData();
      alert("Break ended!");
    } catch (error) {
      alert(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const formatHours = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "checked-in":
        return "text-green-600 bg-green-100";
      case "on-break":
        return "text-yellow-600 bg-yellow-100";
      case "checked-out":
        return "text-gray-600 bg-gray-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "checked-in":
        return "Working";
      case "on-break":
        return "On Break";
      case "checked-out":
        return "Checked Out";
      default:
        return "Not Checked In";
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-8 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-10 bg-gray-200 rounded w-full"></div>
        </div>
      </div>
    );
  }

  const currentStatus = attendanceStatus?.status || "not-checked-in";
  const attendance = attendanceStatus?.attendance;
  const summary = dailySummary?.summary;

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Clock className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-900">Attendance</h3>
          </div>
          <div className="text-right">
            <div className="text-2xl font-mono text-gray-900">
              {formatTime(currentTime)}
            </div>
            <div className="text-sm text-gray-500">
              {currentTime.toLocaleDateString()}
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="mb-6">
          <div
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
              currentStatus
            )}`}
          >
            <div className="w-2 h-2 bg-current rounded-full mr-2 animate-pulse"></div>
            {getStatusText(currentStatus)}
          </div>
        </div>

        {/* Location Status */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm">
              <MapPin className="h-4 w-4 text-gray-500" />
              {locationLoading ? (
                <span className="text-gray-600 flex items-center">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-2"></div>
                  {t("attendance.location.gettingLocation")}
                </span>
              ) : locationError ? (
                <span className="text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {locationError}
                </span>
              ) : location ? (
                <span className="text-green-600 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  {t("attendance.location.available")} (
                  {location.accuracy?.toFixed(0)}m)
                </span>
              ) : (
                <span className="text-gray-500">
                  {t("attendance.location.gettingLocation")}
                </span>
              )}
            </div>
            {locationError && (
              <button
                onClick={() => getLocationWithRetry()}
                disabled={locationLoading}
                className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50 flex items-center"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                {t("attendance.location.retry")}
              </button>
            )}
          </div>
        </div>

        {/* Daily Summary */}
        {summary && (
          <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
            <div>
              <div className="text-sm text-gray-500">Hours Today</div>
              <div className="text-lg font-semibold text-gray-900">
                {formatHours(summary.totalHours || 0)}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Earnings</div>
              <div className="text-lg font-semibold text-green-600">
                ${(summary.grossPay || 0).toFixed(2)}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          {currentStatus === "not-checked-in" && (
            <button
              onClick={handleCheckIn}
              disabled={actionLoading || !location}
              className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Play className="h-4 w-4 mr-2" />
              {actionLoading ? "Checking In..." : "Check In"}
            </button>
          )}

          {currentStatus === "checked-in" && (
            <div className="space-y-2">
              <button
                onClick={() => setShowBreakModal(true)}
                disabled={actionLoading}
                className="w-full flex items-center justify-center px-4 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
              >
                <Coffee className="h-4 w-4 mr-2" />
                Start Break
              </button>

              <button
                onClick={handleCheckOut}
                disabled={actionLoading || !location}
                className="w-full flex items-center justify-center px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <Square className="h-4 w-4 mr-2" />
                {actionLoading ? "Checking Out..." : "Check Out"}
              </button>
            </div>
          )}

          {currentStatus === "on-break" && (
            <button
              onClick={handleEndBreak}
              disabled={actionLoading}
              className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Play className="h-4 w-4 mr-2" />
              {actionLoading ? "Ending Break..." : "End Break"}
            </button>
          )}

          {currentStatus === "checked-out" && (
            <div className="text-center py-4 text-gray-500">
              You have completed your shift for today.
            </div>
          )}
        </div>

        {/* Check-in Time Display */}
        {attendance && attendance.checkIn && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              <div>
                Checked in at:{" "}
                {new Date(attendance.checkIn.timestamp).toLocaleTimeString()}
              </div>
              {attendance.breaks && attendance.breaks.length > 0 && (
                <div>
                  Break time: {Math.round(attendance.totalBreakTime || 0)}{" "}
                  minutes
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Break Modal */}
      {showBreakModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Start Break</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason (optional)
              </label>
              <input
                type="text"
                value={breakReason}
                onChange={(e) => setBreakReason(e.target.value)}
                placeholder="Lunch, coffee, meeting..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                maxLength={100}
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowBreakModal(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleStartBreak}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
              >
                {actionLoading ? "Starting..." : "Start Break"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Check Out Confirmation Modal */}
      {showCheckOutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-sm mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm Check Out
              </h3>
              <button
                onClick={() => setShowCheckOutConfirm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 mb-6">
              Are you sure you want to check out for the day? This will end your
              current work session.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowCheckOutConfirm(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmCheckOut}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? "Checking Out..." : "Check Out"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceWidget;
